import React from 'react';
import Select from 'react-select';
import classNames from 'classnames';
import FieldTextInput from '../FieldTextInput/FieldTextInput';
import { FieldCheckbox } from '../../components';
import { FormattedMessage } from '../../util/reactIntl';
import fulfillmentCss from '../OrderPanel/FeesInputFields/FulfillmentSelectFields.module.css';
import feesCss from '../OrderPanel/FeesInputFields/FeesInputFields.module.css';
import { DELIVERY_FEE, PICKUP_FEE, FLAT_RATE, PERCENTAGE_OF_RENTAL, USAGE_MULTIPLE, REQUIRED_UPFRONT } from '../../util/constants';
import { formatMoney } from '../../util/currency';
import { required } from '../../util/validators';
import { feeTypeMapper } from '../../util/constants';

const SELF_PICKUP = 'self-pickup';
const SELF_RETURN = 'self-return';
const DELIVERY_OPTION = 'delivery';
const PICKUP_OPTION = 'pickup-by-owner';

const getSelectStyles = hasError => ({
  control: (provided, state) => ({
    ...provided,
    minHeight: 48,
    borderRadius: 8,
    borderColor: hasError ? '#f04438' : '#d5d7da',
    boxShadow: 'none',
    cursor: 'pointer',
    '&:hover': { borderColor: hasError ? '#f04438' : '#d5d7da' },
  }),
  placeholder: provided => ({ ...provided, color: '#717680', fontWeight: 500 }),
  singleValue: provided => ({ ...provided, color: '#101828', fontWeight: 500 }),
  menu: provided => ({ ...provided, zIndex: 20 }),
  indicatorSeparator: () => ({ display: 'none' }),
});

const feeDisplayPrice = (fee, intl) => {
  if (!fee) return null;
  if (fee.feeCalculationType === FLAT_RATE) {
    return formatMoney(intl, fee.rate);
  }
  if (fee.feeCalculationType === PERCENTAGE_OF_RENTAL) {
    return intl.formatMessage(
      { id: 'OtherOptionalChargesField.OtherOptionalFees.percentage' },
      { percentage: fee.percentage }
    );
  }
  return null;
};

/**
 * Shared delivery/return selection widget for both the Listing Page cart drawer
 * (CartDrawer) and the Store Profile cart panel (CartModal) - two "Receiving Item" /
 * "Returning Item" dropdowns, matching the Figma mock. Consumed by both because their
 * form-value shape is identical (values.global.optionalFees / values.items.{id}.* /
 * values.pickupAtOwnerLocation), needed because cart items can be different listings
 * from the same store with their own per-item fees. Distinct from OrderPanel's
 * FulfillmentSelectFields (single-item form shape: values.pickupAtOwnerLocation /
 * values.otherOptionalFees / values[DELIVERY_FEE]) used by the regular booking panel,
 * which must stay unaffected by this component.
 *
 * Handles global (once-per-cart) delivery/pickup fees, which is the common case and
 * the only one the Figma mock shows. Per-item fees (a listing with
 * checkoutOptions: CHARGE_FOR_THIS_ITEM_INDIVIDUALLY) are rarer and fall back to
 * simple per-item checkbox rows below the two dropdowns rather than trying to force
 * them into the same two-dropdown shape, which only makes sense for a single global
 * choice; field names (items.{id}.optionalFees/requiredFees/deliveryFee/pickupFee)
 * match what buildItemsForAPI in cartModalHelpers.js already reads, so no
 * submission-side changes are needed by either consumer.
 */
const DeliveryReturnSelectField = props => {
  const { intl, fetchLineItemsInProgress, values, deliveryPickup, deliveryPickupError, form } = props;

  const {
    globalDeliveryFee,
    globalPickupFee,
    perItemDeliveryFees = [],
    perItemPickupFees = [],
    hasDeliveryOption,
    hasPickupOption,
    isDeliveryRequired,
    isPickupRequired,
  } = deliveryPickup || {};

  const shouldHideSelfPickup = isDeliveryRequired;
  const shouldHideSelfReturn = isPickupRequired;

  const isDeliveryUsageBased = globalDeliveryFee?.feeCalculationType === USAGE_MULTIPLE;
  const isPickupUsageBased = globalPickupFee?.feeCalculationType === USAGE_MULTIPLE;

  const hasSelfPickup = values?.pickupAtOwnerLocation?.[0] === true;
  const hasSelfReturn = values?.returnAtOwnerLocation?.[0] === true;

  const deliverySelected =
    isDeliveryRequired ||
    values?.global?.optionalFees?.includes(DELIVERY_FEE) ||
    (isDeliveryUsageBased && Number(values?.global?.deliveryFee) > 0);
  const pickupSelected =
    isPickupRequired ||
    values?.global?.optionalFees?.includes(PICKUP_FEE) ||
    (isPickupUsageBased && Number(values?.global?.pickupFee) > 0);

  const receivingValue = deliverySelected ? DELIVERY_OPTION : hasSelfPickup ? SELF_PICKUP : null;
  const returningValue = pickupSelected ? PICKUP_OPTION : hasSelfReturn ? SELF_RETURN : null;

  const setGlobalOptionalFees = (feeType, add) => {
    const current = values?.global?.optionalFees || [];
    const next = add
      ? Array.from(new Set([...current, feeType]))
      : current.filter(f => f !== feeType);
    form.change('global.optionalFees', next);
  };

  const handleReceivingChange = option => {
    const value = option?.value;
    if (value === SELF_PICKUP) {
      form.change('pickupAtOwnerLocation', [true]);
      setGlobalOptionalFees(DELIVERY_FEE, false);
      if (isDeliveryUsageBased) {
        form.change('global.deliveryFee', 0);
      }
    } else if (value === DELIVERY_OPTION) {
      form.change('pickupAtOwnerLocation', []);
      if (isDeliveryUsageBased) {
        if (!(Number(values?.global?.deliveryFee) > 0)) {
          form.change('global.deliveryFee', 1);
        }
      } else {
        setGlobalOptionalFees(DELIVERY_FEE, true);
      }
    }
  };

  const handleReturningChange = option => {
    const value = option?.value;
    if (value === SELF_RETURN) {
      form.change('returnAtOwnerLocation', [true]);
      setGlobalOptionalFees(PICKUP_FEE, false);
      if (isPickupUsageBased) {
        form.change('global.pickupFee', 0);
      }
    } else if (value === PICKUP_OPTION) {
      form.change('returnAtOwnerLocation', []);
      if (isPickupUsageBased) {
        if (!(Number(values?.global?.pickupFee) > 0)) {
          form.change('global.pickupFee', 1);
        }
      } else {
        setGlobalOptionalFees(PICKUP_FEE, true);
      }
    }
  };

  const isPickupAtOwnerLocation = true;

  // The shared "Receiving Item"/"Returning Item" dropdowns exist to weigh self-pickup
  // against a SHARED (oncePerCart) delivery fee - when no item in the cart has a
  // oncePerCart delivery/pickup fee (only oncePerItem ones, rendered as their own rows
  // below), there's no shared decision to make at all, so the dropdown shouldn't render
  // even a self-pickup-only option. Gating the whole option list on globalDeliveryFee/
  // globalPickupFee (only ever non-null when at least one oncePerCart candidate exists)
  // keeps this consistent with isDeliveryRequired/isPickupRequired, which are derived
  // from the same candidate set.
  const receivingOptions = [];
  if (globalDeliveryFee) {
    if (isPickupAtOwnerLocation && !shouldHideSelfPickup) {
      receivingOptions.push({
        value: SELF_PICKUP,
        label: `${intl.formatMessage({ id: 'OtherBookingFields.selfPickup.label' })} | $0`,
      });
    }
    // Short "Delivery" label, matching FulfillmentSelectFields.js's booking-panel
    // dropdown - same fix, same reasoning (not the generic "Delivery by Owner" phrasing
    // OtherOptionalChargesField.OtherOptionalFees.label uses elsewhere).
    const label = intl.formatMessage({ id: 'FulfillmentSelectFields.deliveryOptionLabel' });
    const price = isDeliveryUsageBased ? null : feeDisplayPrice(globalDeliveryFee, intl);
    receivingOptions.push({
      value: DELIVERY_OPTION,
      label: price ? `${label} | ${price}` : label,
    });
  }

  const returningOptions = [];
  if (globalPickupFee) {
    if (isPickupAtOwnerLocation && !shouldHideSelfReturn) {
      returningOptions.push({
        value: SELF_RETURN,
        label: `${intl.formatMessage({ id: 'OtherBookingFields.selfReturn.label' })} | $0`,
      });
    }
    // Short "Pickup" label - see the matching comment on the delivery option above.
    const label = intl.formatMessage({ id: 'FulfillmentSelectFields.pickupOptionLabel' });
    const price = isPickupUsageBased ? null : feeDisplayPrice(globalPickupFee, intl);
    returningOptions.push({
      value: PICKUP_OPTION,
      label: price ? `${label} | ${price}` : label,
    });
  }

  const isDisabled = fetchLineItemsInProgress;

  const receivingSelectedOption = receivingOptions.find(opt => opt.value === receivingValue) || null;
  const returningSelectedOption = returningOptions.find(opt => opt.value === returningValue) || null;

  // Field names match what buildItemsForAPI already reads (items.{id}.optionalFees/
  // requiredFees with DELIVERY_FEE/PICKUP_FEE as the value, or
  // items.{id}.deliveryFee/pickupFee for usage-multiple), so no submission-side changes
  // are needed here, only the rendering.
  const renderPerItemFees = (fees, fieldKey) =>
    fees.map(fee => {
      const itemId = fee.itemId;
      const itemFormValues = values?.items?.[itemId] || {};
      const isUsageBased = fee.feeCalculationType === USAGE_MULTIPLE;
      const isRequired = fee.whenToApply === REQUIRED_UPFRONT;

      if (isUsageBased) {
        const fieldName = `items.${itemId}.${fieldKey}`;
        const validateMaybe = isRequired
          ? {
              validate: required(
                intl.formatMessage({ id: 'UsageMultipleChargeFieldMaybe.UsageMultipleCharges.required' })
              ),
            }
          : {};

        return (
          <FieldTextInput
            key={`${fieldKey}-${itemId}`}
            type="number"
            className={feesCss.usageField}
            defaultValue={itemFormValues[fieldKey] || (isRequired ? 1 : 0)}
            min={1}
            id={fieldName}
            name={fieldName}
            label={intl.formatMessage(
              { id: 'UsageMultipleChargeFieldMaybe.UsageMultipleCharges.label' },
              {
                feeType: `${feeTypeMapper[fee.feeType]} - ${fee.itemTitle}`,
                text: fee.usageLabel,
                rate: formatMoney(intl, fee.rate),
                isRequired,
              }
            )}
            {...validateMaybe}
            disabled={isDisabled || isRequired}
          />
        );
      }

      const isChecked =
        isRequired ||
        itemFormValues.optionalFees?.includes(fee.feeType) ||
        itemFormValues.requiredFees?.includes(fee.feeType);

      return (
        <div
          key={`${fieldKey}-${itemId}`}
          className={classNames(feesCss.otherFee, isChecked ? feesCss.active : null, isRequired ? feesCss.disabled : null)}
        >
          <FieldCheckbox
            id={`${fieldKey}-${itemId}`}
            name={`items.${itemId}.${isRequired ? 'requiredFees' : 'optionalFees'}`}
            value={fee.feeType}
            disabled={isDisabled || isRequired}
            className={feesCss.checkbox}
            label={
              <div className={feesCss.feeLabel}>
                <div>
                  <div className={feesCss.deliveryText}>
                    {feeTypeMapper[fee.feeType]} - {fee.itemTitle}
                  </div>
                </div>
                <span className={feesCss.priceText}>{feeDisplayPrice(fee, intl)}</span>
              </div>
            }
          />
        </div>
      );
    });

  if (!hasDeliveryOption && !hasPickupOption && !perItemDeliveryFees.length && !perItemPickupFees.length) {
    return null;
  }

  return (
    <div className={fulfillmentCss.container}>
      {deliveryPickupError && (
        <div className={feesCss.deliveryPickupError}>
          <FormattedMessage
            id={
              deliveryPickupError === 'delivery'
                ? 'BookingDatesForm.deliveryMethodRequired'
                : 'BookingDatesForm.returnMethodRequired'
            }
          />
        </div>
      )}
      <div className={fulfillmentCss.row}>
        {receivingOptions.length > 0 && (
          <div className={fulfillmentCss.field}>
            <label className={fulfillmentCss.label} htmlFor="cartDeliveryReturnReceivingItemSelect">
              <FormattedMessage id="FulfillmentSelectFields.receivingItem" />
            </label>
            <div className={fulfillmentCss.selectWrapper}>
              <Select
                inputId="cartDeliveryReturnReceivingItemSelect"
                styles={getSelectStyles(deliveryPickupError === 'delivery')}
                options={receivingOptions}
                value={receivingSelectedOption}
                onChange={handleReceivingChange}
                isDisabled={isDisabled || (shouldHideSelfPickup && receivingOptions.length === 1)}
                isSearchable={false}
                placeholder={intl.formatMessage({ id: 'FulfillmentSelectFields.selectOption' })}
              />
            </div>
            {isDeliveryUsageBased && receivingValue === DELIVERY_OPTION && (
              <FieldTextInput
                type="number"
                className={feesCss.usageField}
                min={1}
                id="cartDeliveryReturnGlobalDeliveryFee"
                name="global.deliveryFee"
                label={intl.formatMessage(
                  { id: 'UsageMultipleChargeFieldMaybe.UsageMultipleCharges.label' },
                  {
                    feeType: feeTypeMapper[DELIVERY_FEE],
                    text: globalDeliveryFee.usageLabel,
                    rate: formatMoney(intl, globalDeliveryFee.rate),
                    isRequired: isDeliveryRequired,
                  }
                )}
                disabled={isDisabled}
              />
            )}
          </div>
        )}
        {returningOptions.length > 0 && (
          <div className={fulfillmentCss.field}>
            <label className={fulfillmentCss.label} htmlFor="cartDeliveryReturnReturningItemSelect">
              <FormattedMessage id="FulfillmentSelectFields.returningItem" />
            </label>
            <div className={fulfillmentCss.selectWrapper}>
              <Select
                inputId="cartDeliveryReturnReturningItemSelect"
                styles={getSelectStyles(deliveryPickupError === 'return')}
                options={returningOptions}
                value={returningSelectedOption}
                onChange={handleReturningChange}
                isDisabled={isDisabled || (shouldHideSelfReturn && returningOptions.length === 1)}
                isSearchable={false}
                placeholder={intl.formatMessage({ id: 'FulfillmentSelectFields.selectOption' })}
              />
            </div>
            {isPickupUsageBased && returningValue === PICKUP_OPTION && (
              <FieldTextInput
                type="number"
                className={feesCss.usageField}
                min={1}
                id="cartDeliveryReturnGlobalPickupFee"
                name="global.pickupFee"
                label={intl.formatMessage(
                  { id: 'UsageMultipleChargeFieldMaybe.UsageMultipleCharges.label' },
                  {
                    feeType: feeTypeMapper[PICKUP_FEE],
                    text: globalPickupFee.usageLabel,
                    rate: formatMoney(intl, globalPickupFee.rate),
                    isRequired: isPickupRequired,
                  }
                )}
                disabled={isDisabled}
              />
            )}
          </div>
        )}
      </div>
      {perItemDeliveryFees.length > 0 && (
        <div className={feesCss.otherFeesContainer}>{renderPerItemFees(perItemDeliveryFees, 'deliveryFee')}</div>
      )}
      {perItemPickupFees.length > 0 && (
        <div className={feesCss.otherFeesContainer}>{renderPerItemFees(perItemPickupFees, 'pickupFee')}</div>
      )}
    </div>
  );
};

export default DeliveryReturnSelectField;
